import { memo, useMemo } from "react";

import { Column, TagTextAlert } from "@bridgesplit/ui";
import {
    BsMetaUtil,
    getLoanRaydiumCollateral,
    getLoanStrategyIdentifier,
    getZcLedger,
    useRaydiumLiquidityTransaction,
    useUserAvailableAssets
} from "@bridgesplit/abf-react";
import { LOADING_ERROR, Result } from "@bridgesplit/utils";
import { TxnManageRaydiumLiquidityParams } from "@bridgesplit/abf-sdk";

import { AppButton } from "../../../../common";
import { useTransactionSender } from "../../../../transactions";
import { useDepositRaydiumBalances } from "./util";
import { useRaydiumContext } from "../RaydiumContext";
import { getMaxBalanceBalanceWarning } from "../../util";
import { getSafeSlippageDecimals } from "../util";

export default memo(function DepositCta() {
    const {
        depositForm,
        addLiquidityQuote,
        poolData,
        raydiumPosition,
        slippageController: { isAutoSlippage }
    } = useRaydiumContext();
    const { A, B } = useDepositRaydiumBalances();

    const addLiquidity = useAddRaydiumLiquidity();
    const { data: userAssets } = useUserAvailableAssets({});

    const rawUserTokenAAmount = useMemo(() => {
        return userAssets?.find((asset) => asset.metadata.mint === raydiumPosition.tokenA.metadata.mint)?.amount ?? 0;
    }, [userAssets, raydiumPosition.tokenA.metadata.mint]);

    const rawUserTokenBAmount = useMemo(() => {
        return userAssets?.find((asset) => asset.metadata.mint === raydiumPosition.tokenB.metadata.mint)?.amount ?? 0;
    }, [userAssets, raydiumPosition.tokenB.metadata.mint]);

    const alertError = useMemo(() => {
        // don't show alert with no user input
        if (depositForm.status === undefined) return undefined;
        if (A.insufficientBalance)
            return `Insufficient ${BsMetaUtil.getSymbol(raydiumPosition.tokenA.metadata)} balance`;
        if (B.insufficientBalance)
            return `Insufficient ${BsMetaUtil.getSymbol(raydiumPosition.tokenB.metadata)} balance`;

        return undefined;
    }, [
        depositForm.status,
        A.insufficientBalance,
        raydiumPosition.tokenA.metadata,
        raydiumPosition.tokenB.metadata,
        B.insufficientBalance
    ]);

    const ctaError = useMemo(() => {
        if (!depositForm.tokenAAmount && !depositForm.tokenBAmount) return "Enter an amount";
        if (depositForm.status === undefined) return "No changes made";
        if (depositForm.status === "error") return "No quote found";
        if (!poolData || !addLiquidityQuote) return "Loading";
        return undefined;
    }, [depositForm.tokenAAmount, depositForm.tokenBAmount, depositForm.status, poolData, addLiquidityQuote]);

    const ctaWarning = useMemo(() => {
        return getMaxBalanceBalanceWarning(
            isAutoSlippage,
            raydiumPosition.tokenA.metadata,
            raydiumPosition.tokenB.metadata,
            depositForm.maxTokenAAmount,
            depositForm.maxTokenBAmount,
            rawUserTokenAAmount,
            rawUserTokenBAmount
        );
    }, [
        depositForm.maxTokenAAmount,
        depositForm.maxTokenBAmount,
        isAutoSlippage,
        rawUserTokenAAmount,
        rawUserTokenBAmount,
        raydiumPosition.tokenA.metadata,
        raydiumPosition.tokenB.metadata
    ]);
    return (
        <Column spacing={1}>
            {!!alertError && (
                <TagTextAlert color="error" icon="warning">
                    {alertError}
                </TagTextAlert>
            )}
            {!alertError && !!ctaWarning && (
                <TagTextAlert color="warning" icon="warning">
                    {ctaWarning}
                </TagTextAlert>
            )}
            <AppButton
                asyncCta={{ onClickWithResult: addLiquidity }}
                isTransaction
                color="secondary"
                disabled={!!ctaError || !!alertError}
            >
                {ctaError ?? "Deposit"}
            </AppButton>
        </Column>
    );
});

function useAddRaydiumLiquidity() {
    const { addLiquidityQuote, loanExpanded, reset, slippageController } = useRaydiumContext();
    const send = useTransactionSender();

    const increaseLiquidity = useRaydiumLiquidityTransaction();
    const zcLedger = getZcLedger(loanExpanded);
    return async () => {
        if (!addLiquidityQuote || !zcLedger) return Result.errFromMessage(LOADING_ERROR);

        const strategyIdentifier = getLoanStrategyIdentifier(loanExpanded);
        if (!strategyIdentifier) return Result.errFromMessage("Updates are not available for this loan");

        const positionMint = getLoanRaydiumCollateral(loanExpanded)?.position.nftMint;
        if (!positionMint) return Result.errFromMessage("No position mint found");

        const slippageDecimals = getSafeSlippageDecimals(slippageController);

        const params: TxnManageRaydiumLiquidityParams = {
            loan: loanExpanded.loan.address,
            positionMint,
            increase: true,
            isTokenA: addLiquidityQuote.isTokenA,
            amount: addLiquidityQuote.amount,
            slippageDecimals,
            manageParams: {
                collateralIndex: 0, //TODO: Account for multiple positions,
                liquidityAmount: addLiquidityQuote.liquidity.toNumber(),
                manageParams: {
                    tokenAAmount: addLiquidityQuote.maxTokenA.toNumber(),
                    tokenBAmount: addLiquidityQuote.maxTokenB.toNumber()
                }
            }
        };

        return await send(increaseLiquidity, params, {
            sendOptions: { onSuccess: reset },
            preventAutoDialogClose: true
        });
    };
}
